import { useState, useCallback, useMemo } from 'react';

/**
 * Manages bulk row selection for a list/table (checkbox column + select-all).
 * Shared by the pipeline list view and the companies list view — the only
 * thing that differs between them is the underlying item list.
 *
 * @param {Array<Object>} items - The currently visible rows (e.g. one page).
 * @param {Object} [options]
 * @param {string} [options.idKey='id'] - Property used as the unique row id.
 * @returns {{
 *   selected: string[],
 *   isSelected: (id: string) => boolean,
 *   toggleOne: (id: string, e?: Event) => void,
 *   toggleAll: () => void,
 *   clearSelection: () => void,
 *   retainOnly: (allowedIds: string[]) => void,
 *   allChecked: boolean,
 *   someChecked: boolean,
 * }}
 */
export function useRowSelection(items, { idKey = 'id' } = {}) {
	const [selected, setSelected] = useState([]);

	const clearSelection = useCallback(() => setSelected([]), []);

	// Prunes the selection to only the given ids — used when the visible item set
	// shrinks (e.g. collapsing an expanded group) so selected-but-hidden ids do
	// not leak into bulk actions.
	const retainOnly = useCallback((allowedIds) => {
		const allow = new Set(allowedIds);
		setSelected((prev) => {
			const next = prev.filter((id) => allow.has(id));
			return next.length === prev.length ? prev : next;
		});
	}, []);

	const allChecked = items.length > 0 && selected.length === items.length;
	const someChecked = selected.length > 0 && selected.length < items.length;

	const toggleAll = useCallback(() => {
		setSelected(allChecked ? [] : items.map((item) => item[idKey]));
	}, [allChecked, items, idKey]);

	const toggleOne = useCallback((id, e) => {
		e?.stopPropagation();
		setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
	}, []);

	const isSelected = useCallback((id) => selected.includes(id), [selected]);

	return useMemo(
		() => ({ selected, isSelected, toggleOne, toggleAll, clearSelection, retainOnly, allChecked, someChecked }),
		[selected, isSelected, toggleOne, toggleAll, clearSelection, retainOnly, allChecked, someChecked],
	);
}
